import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiArrowDown, mdiArrowUp, mdiDelete  } from '@mdi/js'
import { useCan } from '../../contexts/PermissionsContext'
import { LettabilityBlockedError, useCreateSignatureRequest } from '../../hooks/useSignatures'
import { UserSelect } from '../UserSelect'
import { Alert, AlertDescription } from '../ui/alert'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { AppIcon } from '../ui/icon'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Textarea } from '../ui/textarea'
import { SECTION_ICONS } from '../../lib/section-icons'
import { useLocale } from '../../contexts/LocaleContext'
import { getLocalizedName } from '../../lib/localization'
import { LettabilityStatusBadge } from '../occupation/OccupationTab'

interface SignerDraft {
    userUid: string
    name: string
    role: string
    identityLevel: 'otp' | 'strong'
}

interface Props {
    org: string
    documentUid: string
    documentName: string
    /** The act the signature carries; the document itself by default. */
    subject?: { type: 'contract' | 'work_order'; uid: string }
}

const defaultExpiry = () => {
    const d = new Date()
    d.setDate(d.getDate() + 30)
    return d.toISOString().slice(0, 10)
}

/**
 * "Demander une signature" on a PDF (spec 2026-10-05-05 §5): the signers are
 * contacts, each with a role and an identification level; they sign in the
 * listed order when "one after the other" is ticked, otherwise all at once.
 * The request leaves as soon as it is confirmed.
 */
export function RequestSignatureDialog({ org, documentUid, documentName, subject }: Props) {
    const { t } = useTranslation('signatures')
    const canWrite = useCan('signatures.write')
    const [open, setOpen] = useState(false)
    const [title, setTitle] = useState(documentName)
    const [message, setMessage] = useState('')
    const [ordered, setOrdered] = useState(false)
    const [expiresOn, setExpiresOn] = useState(defaultExpiry)
    const [signers, setSigners] = useState<SignerDraft[]>([])
    const [picker, setPicker] = useState('')
    const [lettabilityReason, setLettabilityReason] = useState('')
    const create = useCreateSignatureRequest(org)
    const { locale } = useLocale()
    const tLeases = useTranslation('leases').t
    const blocked = create.error instanceof LettabilityBlockedError ? create.error : null

    if (!canWrite) return null

    const reset = () => {
        setTitle(documentName)
        setMessage('')
        setOrdered(false)
        setExpiresOn(defaultExpiry())
        setSigners([])
        setPicker('')
        setLettabilityReason('')
        create.reset()
    }

    const move = (i: number, delta: number) => {
        const next = signers.slice()
        const [s] = next.splice(i, 1)
        next.splice(i + delta, 0, s)
        setSigners(next)
    }

    const submit = async (confirmWith?: string) => {
        const expires = new Date(`${expiresOn}T23:59:59`)
        await create.mutateAsync({
            lettability_confirmation: confirmWith ? { reason: confirmWith } : undefined,
            document_uid: documentUid,
            title: title.trim(),
            message: message.trim() || undefined,
            ordered,
            expires_at: expires.toISOString(),
            subject_type: subject?.type,
            subject_uid: subject?.uid,
            send: true,
            signers: signers.map((s) => ({ user_uid: s.userUid, role: s.role.trim() || undefined, identity_level: s.identityLevel })),
        })
        setOpen(false)
        reset()
    }
    const trySubmit = (confirmWith?: string) => submit(confirmWith).catch(() => undefined)

    return (
        <>
            <Button
                variant="outline"
                className="p-2"
                onClick={() => setOpen(true)}
                aria-label={t('request.action')}
                title={t('request.action')}
                data-testid="request-signature-btn"
            >
                <AppIcon path={SECTION_ICONS.signatures} size="md" />
            </Button>
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset() }}>
                <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="request-signature-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('request.title')}</DialogTitle>
                        <DialogDescription>{t('request.description')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1">
                            <Label htmlFor="sig-title">{t('request.field_title')}</Label>
                            <Input id="sig-title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="request-signature-title" />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="sig-message">{t('request.field_message')}</Label>
                            <Textarea id="sig-message" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="request-signature-message" />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('request.field_signers')}</Label>
                            <UserSelect
                                org={org}
                                value={picker}
                                onValueChange={setPicker}
                                onOptionSelect={(opt) => {
                                    if (!opt?.value || signers.some((s) => s.userUid === opt.value)) return
                                    setSigners([...signers, { userUid: String(opt.value), name: String(opt.label ?? ''), role: '', identityLevel: 'otp' }])
                                    setPicker('')
                                }}
                                placeholder={t('request.add_signer')}
                                data-testid="request-signature-add-signer"
                            />
                            {signers.length === 0 && <p className="text-sm text-muted-foreground">{t('request.no_signer')}</p>}
                            <ol className="space-y-2" data-testid="request-signature-signers">
                                {signers.map((s, i) => (
                                    <li key={s.userUid} className="flex flex-wrap items-center gap-2 rounded-md border p-2" data-testid={`request-signature-signer-${i}`}>
                                        <span className="w-6 text-sm text-muted-foreground">{i + 1}.</span>
                                        <span className="min-w-32 flex-1 text-sm font-medium">{s.name}</span>
                                        <Input
                                            className="w-44"
                                            placeholder={t('request.role_placeholder')}
                                            value={s.role}
                                            onChange={(e) => setSigners(signers.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)))}
                                            data-testid={`request-signature-signer-role-${i}`}
                                        />
                                        <Select
                                            value={s.identityLevel}
                                            onValueChange={(v) => setSigners(signers.map((x, j) => (j === i ? { ...x, identityLevel: v as 'otp' | 'strong' } : x)))}
                                        >
                                            <SelectTrigger className="w-44" data-testid={`request-signature-signer-level-${i}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="otp">{t('identity.otp')}</SelectItem>
                                                <SelectItem value="strong">{t('identity.strong')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Button type="button" variant="ghost" size="icon" disabled={i === 0} onClick={() => move(i, -1)} aria-label={t('request.move_up')} title={t('request.move_up')} data-testid={`request-signature-signer-up-${i}`}>
                                            <AppIcon path={mdiArrowUp} size="sm" />
                                        </Button>
                                        <Button type="button" variant="ghost" size="icon" disabled={i === signers.length - 1} onClick={() => move(i, 1)} aria-label={t('request.move_down')} title={t('request.move_down')} data-testid={`request-signature-signer-down-${i}`}>
                                            <AppIcon path={mdiArrowDown} size="sm" />
                                        </Button>
                                        <Button type="button" variant="ghost" size="icon" onClick={() => setSigners(signers.filter((_, j) => j !== i))} aria-label={t('request.remove')} title={t('request.remove')} data-testid={`request-signature-signer-remove-${i}`}>
                                            <AppIcon path={mdiDelete} size="sm" />
                                        </Button>
                                    </li>
                                ))}
                            </ol>
                        </div>
                        <div className="flex flex-wrap items-center gap-6">
                            <div className="flex items-center gap-2">
                                <Checkbox id="sig-ordered" checked={ordered} onCheckedChange={(v) => setOrdered(v === true)} data-testid="request-signature-ordered" />
                                <Label htmlFor="sig-ordered" className="font-normal">{t('request.field_ordered')}</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Label htmlFor="sig-expires">{t('request.field_expires')}</Label>
                                <Input id="sig-expires" type="date" className="w-44" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} data-testid="request-signature-expires" />
                            </div>
                        </div>
                        {blocked ? (
                            <div className="space-y-2 rounded-md border border-destructive/40 p-3" data-testid="signature-lettability-blocked">
                                <p className="text-sm font-medium">{t('request.lettability.title')}</p>
                                <ul className="space-y-1 text-sm">
                                    {blocked.results.filter((r) => r.status !== 'allowed').map((r) => (
                                        <li key={r.asset_uid} className="space-y-1" data-testid={`signature-lettability-lot-${r.asset_uid}`}>
                                            <LettabilityStatusBadge status={r.status} />
                                            {r.blockers.map((b) => (
                                                <div key={`${b.kind}-${b.code}`} className="text-xs" data-testid={`signature-lettability-blocker-${b.kind}-${b.code}`}>
                                                    {getLocalizedName((b.label ?? {}) as Record<string, string>, locale) || b.code}
                                                </div>
                                            ))}
                                            {r.reasons.map((x) => (
                                                <div key={x.code} className="text-xs text-muted-foreground" data-testid={`signature-lettability-reason-${x.code}`}>
                                                    {tLeases(`occupation.reason.${x.code}`)}
                                                </div>
                                            ))}
                                        </li>
                                    ))}
                                </ul>
                                <Label htmlFor="sig-lettability-reason">{t('request.lettability.reason')}</Label>
                                <Textarea id="sig-lettability-reason" value={lettabilityReason} onChange={(e) => setLettabilityReason(e.target.value)}
                                    data-testid="signature-lettability-reason" />
                                <p className="text-xs text-muted-foreground">{t('request.lettability.reason_help')}</p>
                            </div>
                        ) : create.error && (
                            <Alert variant="destructive" data-testid="request-signature-error">
                                <AlertDescription>{(create.error as Error).message}</AlertDescription>
                            </Alert>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} data-testid="request-signature-cancel">{t('request.cancel')}</Button>
                        {blocked ? (
                            <Button variant="destructive" onClick={() => void trySubmit(lettabilityReason.trim())}
                                disabled={create.isPending || lettabilityReason.trim().length < 10} data-testid="signature-lettability-confirm">
                                {t('request.lettability.confirm')}
                            </Button>
                        ) : (
                            <Button onClick={() => void trySubmit()} disabled={create.isPending || signers.length === 0 || !title.trim()} data-testid="request-signature-submit">
                                {t('request.submit')}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
